/**
 * Read a stamped or printed VIN with Tesseract.
 *
 * The previous scanner mean-thresholded the crop before OCR. That is lethal
 * on a windshield plate: the crop includes dark dash and bright glass, the
 * mean falls in between, and the metal VIN goes uniformly white. Tesseract
 * then returns nothing, which looked identical to "pointed at a blank wall."
 *
 * Preprocess instead: contrast-stretch, local (adaptive) threshold, and the
 * inverted plate — and let Tesseract see the un-thresholded gray as well.
 */

import { VIN_ALPHABET, readVin, type VinRead } from "./parse";

type OcrWorker = {
  recognize: (image: HTMLCanvasElement) => Promise<{ data: { text: string } }>;
  setParameters: (params: Record<string, string>) => Promise<unknown>;
  terminate: () => Promise<void>;
};

let workerPromise: Promise<OcrWorker> | null = null;

export function warmupOcr(): Promise<OcrWorker> {
  if (workerPromise) return workerPromise;
  workerPromise = (async () => {
    const { createWorker } = await import("tesseract.js");
    const worker = (await createWorker("eng")) as unknown as OcrWorker;
    await worker.setParameters({
      tessedit_char_whitelist: VIN_ALPHABET,
      tessedit_pageseg_mode: "7",
      user_defined_dpi: "300",
      preserve_interword_spaces: "1",
    });
    return worker;
  })();
  workerPromise.catch(() => {
    workerPromise = null;
  });
  return workerPromise;
}

export async function terminateOcr(): Promise<void> {
  const p = workerPromise;
  workerPromise = null;
  if (!p) return;
  try {
    const w = await p;
    await w.terminate();
  } catch {
    /* already dead */
  }
}

function luminance(data: Uint8ClampedArray, i: number): number {
  return 0.299 * data[i]! + 0.587 * data[i + 1]! + 0.114 * data[i + 2]!;
}

function grayOf(canvas: HTMLCanvasElement): { gray: Float32Array; w: number; h: number } | null {
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return null;
  const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const gray = new Float32Array(canvas.width * canvas.height);
  for (let p = 0, i = 0; p < gray.length; p++, i += 4) gray[p] = luminance(img.data, i);
  return { gray, w: canvas.width, h: canvas.height };
}

function writeGray(canvas: HTMLCanvasElement, gray: ArrayLike<number>, w: number, h: number) {
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return;
  const img = ctx.createImageData(w, h);
  for (let p = 0, i = 0; p < gray.length; p++, i += 4) {
    const v = Math.max(0, Math.min(255, Math.round(gray[p]!)));
    img.data[i] = img.data[i + 1] = img.data[i + 2] = v;
    img.data[i + 3] = 255;
  }
  canvas.width = w;
  canvas.height = h;
  ctx.putImageData(img, 0, 0);
}

function contrastStretch(gray: Float32Array): Float32Array {
  const sorted = Float32Array.from(gray).sort();
  const p2 = sorted[Math.floor(sorted.length * 0.02)] ?? 0;
  const p98 = sorted[Math.floor(sorted.length * 0.98)] ?? 255;
  const span = Math.max(8, p98 - p2);
  const out = new Float32Array(gray.length);
  for (let i = 0; i < gray.length; i++) {
    out[i] = ((gray[i]! - p2) / span) * 255;
  }
  return out;
}

function adaptiveThreshold(gray: Float32Array, w: number, h: number, win = 21, c = 8): Float32Array {
  const out = new Float32Array(gray.length);
  const r = Math.max(4, Math.floor(win / 2));
  // Integral image for O(1) local means.
  const integ = new Float64Array((w + 1) * (h + 1));
  for (let y = 1; y <= h; y++) {
    let row = 0;
    for (let x = 1; x <= w; x++) {
      row += gray[(y - 1) * w + (x - 1)]!;
      integ[y * (w + 1) + x] = integ[(y - 1) * (w + 1) + x]! + row;
    }
  }
  const sumAt = (x0: number, y0: number, x1: number, y1: number) => {
    const A = integ[y0 * (w + 1) + x0]!;
    const B = integ[y0 * (w + 1) + x1]!;
    const C = integ[y1 * (w + 1) + x0]!;
    const D = integ[y1 * (w + 1) + x1]!;
    return D - B - C + A;
  };
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const x0 = Math.max(0, x - r);
      const y0 = Math.max(0, y - r);
      const x1 = Math.min(w, x + r + 1);
      const y1 = Math.min(h, y + r + 1);
      const n = (x1 - x0) * (y1 - y0);
      const mean = sumAt(x0, y0, x1, y1) / Math.max(1, n);
      out[y * w + x] = gray[y * w + x]! < mean - c ? 0 : 255;
    }
  }
  return out;
}

function invert(gray: Float32Array): Float32Array {
  const out = new Float32Array(gray.length);
  for (let i = 0; i < gray.length; i++) out[i] = 255 - gray[i]!;
  return out;
}

function variantCanvas(source: HTMLCanvasElement, kind: "gray" | "stretch" | "adaptive" | "invert"): HTMLCanvasElement {
  const out = document.createElement("canvas");
  const g = grayOf(source);
  if (!g) {
    out.width = source.width;
    out.height = source.height;
    out.getContext("2d")?.drawImage(source, 0, 0);
    return out;
  }
  let pixels: Float32Array = g.gray;
  if (kind === "stretch" || kind === "invert") pixels = contrastStretch(g.gray);
  if (kind === "invert") pixels = invert(pixels);
  if (kind === "adaptive") pixels = adaptiveThreshold(contrastStretch(g.gray), g.w, g.h);
  writeGray(out, pixels, g.w, g.h);
  return out;
}

export async function ocrVin(
  canvas: HTMLCanvasElement,
): Promise<{ read: VinRead | null; text: string }> {
  const worker = await warmupOcr();
  const variants: Array<"gray" | "stretch" | "adaptive" | "invert"> = [
    "stretch",
    "gray",
    "adaptive",
    "invert",
  ];
  const modes = ["7", "6"];
  let lastText = "";

  for (const psm of modes) {
    await worker.setParameters({
      tessedit_char_whitelist: VIN_ALPHABET,
      tessedit_pageseg_mode: psm,
      user_defined_dpi: "300",
    });
    for (const kind of variants) {
      const img = variantCanvas(canvas, kind);
      try {
        const { data } = await worker.recognize(img);
        const text = (data.text ?? "").trim();
        if (text) lastText = text;
        const read = readVin(data.text ?? "");
        if (read) return { read, text };
      } catch {
        /* try the next variant */
      }
    }
  }
  return { read: null, text: lastText };
}
